import React, { useState } from "react";
import SlideOutSidebar from "../../../ui-components/slide-out-sidebar/slideOutSidebar";
import { useEquipmentListDetails, useDeleteEquipment, useTerminalsList } from "hooks/useEquipment";
import XGSErrorMessage from "ui-components/error-message/errorMessage";
import { EquipmentDetailModel, isEquipmentTypeWithoutVehicleIds } from "app/data/equipment/models";
import "./equipmentAddEditDetails.scss";
import { Card } from "ui-components/card/card";
import Loading from "ui-components/loading/loading";
import UserState from "slices/user/UserState";
import { userSelector } from "slices/user/userSlice";
import { ReactComponent as EditIcon } from "images/edit_solid.svg";
import { useSelector } from "react-redux";
import Button, { ButtonThemes } from "ui-components/button/button";
import Tag from "ui-components/molecules/tag/tag";
import { TagColor } from "app/data/common/tagColor";
import EquipmentAddEditDetails from "./equipmentAddEditDetails";
import { toast } from "react-toastify";
import { ReactComponent as AlertIcon } from "images/alert.svg";
import ConfirmationModal from "ui-components/confirmation-modal/confirmationModal";
import ProgressBar from "ui-components/progress-bar/progressBar";
import { Popover } from "ui-components/popover/popover";
import { ReactComponent as FileIcon } from "images/file-grey.svg";
import EquipmentBadgeDownloadButton from "./EquipmentBadgeDownloadButton";
import { formatEquipmentTerminalLabel } from "../utils/downloadEquipmentBadge";
import { canEditEquipmentAtTerminal } from "../utils/canEditEquipmentAtTerminal";
import { UserUtils } from "app/data/user/userUtils";
import EquipmentChangeLog from "./section/equipmentChangeLog";

interface EquipmentInfo {
    id: string;
    unitNumber: string;
    equipmentType: string;
    assetDescriptor: string;
    make: string;
    model: string;
    year: string;
    vin: string;
    licensePlate: string;
    registrationDate: string;
    dotInspectionDate: string;
    domicileTerminal: string;
    location: string;
    garageCity: string;
    garageState: string;
    status: string;
    disposition: string;
    utilizationPercent: string;
    dwellTimeDays: string;
    leaseType: string;
    vendor: string;
    productLine: string;
    notes: string;
    registrationDocUrl: string;
    dotCertificateUrl: string;
    assetTag: string;
    employeeAssigned: string;
    assignedDate: string;
    purchaseDate: string;
    lastCheckIn: string;
}

interface EquipmentViewDetailsProps {
    show: boolean;
    onClose: () => void;
    equipmentNumber?: string;
    onDataUpdated?: () => void;
    onEdit?: (id: string) => void;
    onEditClose?: () => void;
}

const toDateTimeLocalValue = (value?: string | null): string => {
    if (!value?.trim()) {
        return "";
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return value.length >= 16 ? value.slice(0, 16) : value;
    }

    const pad = (n: number) => String(n).padStart(2, "0");
    return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
};

const mapEquipmentToFormInfo = (equipment: EquipmentDetailModel): EquipmentInfo => ({
    id: equipment?.id || "",
    unitNumber: equipment?.unitNumber || "",
    equipmentType: equipment.equipmentType || "",
    assetDescriptor: equipment.assetDescriptor || "",
    make: equipment.make || "",
    model: equipment.model || "",
    year: equipment.year || "",
    vin: equipment.vin || "",
    licensePlate: equipment.licensePlate || "",
    registrationDate: equipment.registrationDate || "",
    dotInspectionDate: equipment.dotInspectionDate || "",
    domicileTerminal: equipment.domicileTerminal || "",
    location: equipment.location || "",
    garageCity: equipment.garageCity || "",
    garageState: equipment.garageState || "",
    status: equipment.status || "",
    disposition: equipment.disposition || "",
    utilizationPercent: equipment.utilizationPercent || "",
    dwellTimeDays: equipment.dwellTimeDays || "",
    leaseType: equipment.leaseType || "",
    vendor: equipment.vendor || "",
    productLine: equipment.productLine || "",
    notes: equipment.notes || "",
    registrationDocUrl: equipment.registrationDocUrl || "",
    dotCertificateUrl: equipment.dotCertificateUrl || "",
    assetTag: equipment.assetTag || "",
    employeeAssigned: equipment.employeeAssigned || "",
    assignedDate: equipment.assignedDate || "",
    purchaseDate: equipment.purchaseDate || "",
    lastCheckIn: toDateTimeLocalValue(equipment.lastCheckIn),
})

const EquipmentViewDetails: React.FC<EquipmentViewDetailsProps> = ({ show, onClose, equipmentNumber, onDataUpdated, onEdit, onEditClose }) => {
    const userState: UserState = useSelector(userSelector);
    const { data, isLoading, error, refetch } = useEquipmentListDetails(equipmentNumber);
    const { data: terminals = [] } = useTerminalsList();
    const [isEditing, setIsEditing] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

    const { mutateAsync: deleteEquipment } = useDeleteEquipment();

    const uploadedDocuments = [];

    const equipment: EquipmentDetailModel | undefined = data;
    const matchedTerminal = terminals.find(
        (terminal) => terminal.terminalName === equipment?.location
    );
    const terminalLabel = matchedTerminal
        ? formatEquipmentTerminalLabel(matchedTerminal)
        : (equipment?.location || "");
    const canEdit = canEditEquipmentAtTerminal(
        equipment?.location,
        userState.activeTerminal?.name
    );



    if (equipment?.registrationDocUrl) {
        uploadedDocuments.push({
            name: "Registration Document",
            fileUrl: equipment.registrationDocUrl,
        });
    }

    if (
        equipment?.dotCertificateUrl &&
        !isEquipmentTypeWithoutVehicleIds(equipment?.equipmentType)
    ) {
        uploadedDocuments.push({
            name: "DOT Inspection Certificate",
            fileUrl: equipment.dotCertificateUrl,
        });
    }

    if (!equipmentNumber) return null;


    const renderInfoField = (label: string | React.ReactNode, value: string | number | boolean | undefined | null | React.ReactNode, isValid?: string) => (
        <div className="info-field">
            <div className="info-field__content-wrapper">
                <span className="info-field__label">{label}</span>
                <span className="info-field__value">{value ? value : "N/A"}</span>
            </div>
            {isValid !== undefined && (
                <Tag mods={{ color: isValid === "VALID" ? TagColor.GREEN : TagColor.ORANGE, uppercase: true }}>
                    {isValid}
                </Tag>
            )}
        </div>
    );

    const renderSimpleCardContent = (content: string | JSX.Element | null) => (
        <div className="simple-card-content">
            {content && typeof content === "string" && content.trim() !== "" && content.trim().toUpperCase() !== "NULL"
                ? content
                : <span>No notes available.</span>
            }</div>
    );

    const handleEditClick = () => {
        if (!equipment) return;
        if (!canEditEquipmentAtTerminal(equipment.location, userState.activeTerminal?.name)) {
            return;
        }
        setIsEditing(true);
        if (onEdit) onEdit(equipment.id);
    };

    const handleEditClose = () => {
        setIsEditing(false);
        refetch();
        if (onDataUpdated) onDataUpdated();
        if (onEditClose) onEditClose();
    };

    const handleOpenDeleteConfirmation = () => {
        if (equipmentNumber) {
            setShowDeleteConfirmation(true);
        }
    };

    const handleConfirmDelete = async () => {
        if (!equipmentNumber) return;

        setShowDeleteConfirmation(false);

        try {
            await deleteEquipment(equipmentNumber);
            toast.info(`Equipment ${equipment?.unitNumber || equipmentNumber} deactivated successfully!`);
            onClose();
            if (onDataUpdated) onDataUpdated();
        } catch (err) {
            toast.error("Failed to deactivate equipment");
        }
    };


    if (isEditing && equipment) {
        const initialEquipmentData = mapEquipmentToFormInfo(equipment);
        return (
            <EquipmentAddEditDetails
                show={show}
                onClose={handleEditClose}
                initialEquipmentData={initialEquipmentData}
            />
        );
    }

    const DocumentField: React.FC<{ fileName: string, fileUrl: string }> = ({ fileName, fileUrl }) => (
        <div className="document-field">
            <div className="document-field__info">
                <FileIcon className="document-field__icon" />
                <span className="document-field__name">{fileName}</span>
            </div>
            <a
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="document-field__view-link"
            >
                View
            </a>
        </div>
    );

    return (
        <>
            <SlideOutSidebar
                header={`Unit Number ${equipment?.unitNumber ? equipment?.unitNumber : ""}` || "Equipment Details"}
                onClose={onClose}
                show={show}
                size="small"
            >
                {isLoading && <div className="loading"><Loading isLoading={true} /></div>}

                {error && <XGSErrorMessage>{(error as any)?.message || "Error loading equipment details"}</XGSErrorMessage>}

                {equipment && (
                    <div className="equipment-view-container">
                        <div className="profile-header-equipment__title">
                            <span className="profile-header-equipment__title-text">{"Equipment Information"}</span>
                            {canEdit ? (
                                <div className="edit" onClick={handleEditClick}>
                                    <EditIcon />
                                    <span>Edit</span>
                                </div>
                            ) : (
                                <span className="profile-header-equipment__edit-blocked">
                                    This equipment is assigned to {equipment.location || "another terminal"}. Switch to that terminal to edit.
                                </span>
                            )}
                        </div>

                        <Card hideButtons={true} mix="euipment-info-card registration-id-card">
                            <div className="info-field info-field--with-action">
                                <div className="info-field__content-wrapper">
                                    <span className="info-field__label">Unit Number</span>
                                    <span className="info-field__value">{equipment?.unitNumber || "N/A"}</span>
                                </div>
                                {equipment?.unitNumber && !isEquipmentTypeWithoutVehicleIds(equipment?.equipmentType) && (
                                    <EquipmentBadgeDownloadButton
                                        unitNumber={equipment.unitNumber}
                                        equipmentType={equipment.equipmentType}
                                        terminalLabel={terminalLabel}
                                    />
                                )}
                            </div>
                            {renderInfoField("Type", equipment?.equipmentType)}
                            {renderInfoField(
                                <>
                                    Asset Descriptor{" "}
                                    <Popover
                                        values={
                                            <div className="popover-value">
                                                Describes the asset configuration, like Sleeper, Straight Truck, or TADC (Tandem Axle).
                                            </div>
                                        }
                                    />
                                </>, equipment?.assetDescriptor)}
                            {renderInfoField("Make", equipment?.make)}
                            {renderInfoField("Model", equipment?.model)}
                            {renderInfoField(
                                <>
                                    Year{" "}
                                    <Popover
                                        values={
                                            <div className="popover-value">
                                                Model year of the equipment.
                                            </div>
                                        }
                                    />
                                </>, equipment?.year)}
                            {!isEquipmentTypeWithoutVehicleIds(equipment?.equipmentType) && renderInfoField(<>
                                VIN (Vehicle ID){" "}
                            </>,
                                <>
                                    {"..."}{equipment?.vin?.slice(-5)}{" "}
                                    <Popover
                                        values={
                                            <div className="popover-value">
                                                {"Full VIN: "}{equipment?.vin}
                                            </div>
                                        }
                                    />
                                </>)}
                        </Card >

                        <Card title="Registration & Compliance" hideButtons={true}>
                            {!isEquipmentTypeWithoutVehicleIds(equipment?.equipmentType) &&
                                renderInfoField("License Plate", equipment?.licensePlate)}
                            {renderInfoField("Registered On", equipment?.registrationDate?.toCustomDateFormat("MM/DD/YYYY"))}
                            {!isEquipmentTypeWithoutVehicleIds(equipment?.equipmentType) && renderInfoField(
                                <>
                                    DOT Inspection Date{" "}
                                    <Popover
                                        values={
                                            < div className="popover-value">
                                                Next scheduled Department of Transportation inspection date.
                                            </div>
                                        }
                                    />
                                </>, equipment?.dotInspectionDate?.toCustomDateFormat("MM/DD/YYYY"))}
                        </Card >

                        {isEquipmentTypeWithoutVehicleIds(equipment?.equipmentType) && (
                            <Card title="Asset Assignment" hideButtons={true}>
                                {renderInfoField("Asset Tag", equipment?.assetTag)}
                                {renderInfoField("Employee Assigned", equipment?.employeeAssigned)}
                                {renderInfoField("Assigned Date", equipment?.assignedDate?.toCustomDateFormat("MM/DD/YYYY"))}
                                {renderInfoField("Purchase Date", equipment?.purchaseDate?.toCustomDateFormat("MM/DD/YYYY"))}
                                {renderInfoField("Last Check-In", equipment?.lastCheckIn)}
                            </Card>
                        )}

                        <Card title="Assignment & Utilization" hideButtons={true} mix="assignment-utilization-card">
                            {renderInfoField(
                                <>
                                    Terminal{" "}
                                    <Popover
                                        values={
                                            <div className="popover-value">
                                                Terminal or hub where this equipment is currently assigned.
                                            </div>
                                        }
                                    />
                                </>, equipment?.location)}
                            {renderInfoField("Service Status",
                                <Tag mods={{
                                    color: equipment?.status?.toUpperCase() === "ACTIVE" ? TagColor.GREEN : TagColor.GREY
                                }}>
                                    {equipment?.status}
                                </Tag>)}
                            {!isEquipmentTypeWithoutVehicleIds(equipment?.equipmentType) && (
                                <div className="utilization-row">
                                    <span className="utilization-row__label">Utilization Time</span>
                                    <div className="utilization-row__progress-bar">
                                        <ProgressBar
                                            percentage={equipment?.utilizationPercent ? parseFloat(equipment.utilizationPercent) : 0}
                                            additionalInfo={""}
                                            multicolor
                                        />
                                    </div>
                                    <div className="utilization-row__indicators">
                                        <span className="indicator low">Low 0-29%</span>
                                        <span className="indicator moderate">Moderate 30-59%</span>
                                        <span className="indicator healthy">Healthy 60-100%</span>
                                    </div>
                                </div>
                            )}
                        </Card>

                        <Card title="Ownership" hideButtons={true}>
                            {renderInfoField("Lease Type", equipment?.leaseType)}
                            {renderInfoField("Vendor", equipment?.vendor)}
                            {renderInfoField("Product Line", equipment?.productLine)}
                        </Card>

                        <Card title="Uploaded Documents" hideButtons={true} mix="document-card-layout">
                            <div className="document-card-layout__content">
                                {uploadedDocuments.length > 0 ? (
                                    uploadedDocuments.map((doc, index) => (
                                        <DocumentField
                                            key={index}
                                            fileName={doc.name}
                                            fileUrl={doc.fileUrl}
                                        />
                                    ))
                                ) : (
                                    <div className="no-documents-placeholder">
                                        No documents uploaded yet
                                    </div>
                                )}
                            </div>
                        </Card>

                        <Card title="Notes" hideButtons={true}>
                            {renderSimpleCardContent(equipment?.notes)}
                        </Card>

                        {equipment?.id && (
                            <EquipmentChangeLog equipmentId={equipment.id} />
                        )}

                        {UserUtils.isXGSAdministrator(userState.profile)
                            && equipment?.status?.toUpperCase() !== "INACTIVE" && (
                            <div className="delete-button-container">
                                <Button className="delete-button" onClick={handleOpenDeleteConfirmation} theme={ButtonThemes.red}>Deactivate</Button>
                            </div>
                        )}
                    </div >
                )}
            </SlideOutSidebar >
            <ConfirmationModal
                opened={showDeleteConfirmation}
                onCancel={() => setShowDeleteConfirmation(false)}
                onConfirm={handleConfirmDelete}
            >
                <div className="confirmation-modal__content">
                    <AlertIcon />
                    <div>
                        Are you sure you want to deactivate the equipment:<br />
                        **{equipment?.unitNumber}** ?
                    </div>
                </div>
            </ConfirmationModal>
        </>
    );
};

export default EquipmentViewDetails;
